import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PWAThunk } from 'lib/core/data'
import { Callback, ShippingMethodInput } from 'lib/types'
import { setCart } from '../cart'
import { Slice, ShippingMethodSlice } from './types'

export const initialState: Slice = {}

interface ActionData {
  cartId: string
}

const getSlice = (slice: ShippingMethodSlice): ShippingMethodSlice => {
  return slice ?? ({
    isFetching: false,
    isSuccess: false,
    isFailure: false
  })
}

const slice = createSlice({
  name: 'quote/setShippingMethod',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = true
      state[cartId] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = false
      slice.isSuccess = true
      slice.isFailure = false
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { cartId } = action.payload
      const slice = getSlice(state[cartId])
      slice.isFetching = false
      slice.isSuccess = false
      slice.isFailure = true
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function setShippingMethod (cartId: string, shippingMethod: ShippingMethodInput, callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    dispatch(request({ cartId }))
    try {
      const response = await magentoClient.getClient().quote.setShippingMethod({
        cartId,
        shippingMethod
      })

      const { data } = response
      const { setShippingMethodsOnCart } = data
      const { cart } = setShippingMethodsOnCart

      if (cart) {
        dispatch(setCart({
          cartId,
          data: cart,
          merge: true
        }))
        dispatch(success({ cartId }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ cartId }))
        callback?.onFailure?.({
          error: new Error(`[Shipping Method] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: {
            cartId,
            shippingMethod
          }
        })
      }

      return response
    } catch (e) {
      dispatch(failure({ cartId }))
      callback?.onFailure?.({
        error: new Error(`[Shipping Method] ${e.message}`),
        extraData: {
          cartId,
          shippingMethod
        }
      })
    }
  }
}
